/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AuditDataModel, DevelopmentDetailDataModel, DevelopmentDetailDocumentResponse, PbiDecompositionModel } from '@/domain/models/development.js';
import type { SolicitationEvidenceProps } from '@/domain/models/solicitation-evidence.js';
import type { DevelopmentController } from '@/presentation/controllers/development-controller.js';
import type { DevelopmentDetailController } from '@/presentation/controllers/development-detail-controller.js';
import { useJiraSyncPolling } from '@/presentation/hooks/use-jira-sync-polling.js';
import { isJiraSyncPending } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import { PBI_DECOMPOSITION_ENABLED } from '@/shared/constants/feature-flags.js';
import type { Complexity, DevelopmentCategory } from '@/shared/utils/format-status.js';

type State = {
    data: DevelopmentDetailDataModel | null;
    auditData: AuditDataModel | null;
    pbiDecomposition: PbiDecompositionModel | null;
    cfEvidences: DevelopmentDetailDocumentResponse[];
    ucEvidences: DevelopmentDetailDocumentResponse[];
    isLoading: boolean;
    error: string | null;
};

const POLL_INTERVAL_MS = 5000;

export function useDevelopmentDetail(devController: DevelopmentController, detailController: DevelopmentDetailController, id: string) {
    const [state, setState] = useState<State>({
        data: null,
        auditData: null,
        pbiDecomposition: null,
        cfEvidences: [],
        ucEvidences: [],
        isLoading: true,
        error: null
    });
    const [analysisJustCompleted, setAnalysisJustCompleted] = useState(false);
    const [uploadingEvidences, setUploadingEvidences] = useState(false);
    const previousStatusRef = useRef<string | null>(null);

    const loadPbiDecomposition = useCallback(async (): Promise<PbiDecompositionModel | null> => {
        if (!PBI_DECOMPOSITION_ENABLED) {
            return null;
        }
        const pbiResult = await detailController.getPbiDecomposition(id);
        return pbiResult.isRight() ? pbiResult.value : null;
    }, [detailController, id]);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const [result, auditResult, pbiDecomposition, cfEvidencesResult, ucEvidencesResult] = await Promise.all([
            devController.loadDevelopmentById(id),
            detailController.loadAudit(id),
            loadPbiDecomposition(),
            detailController.listCfEvidences(id),
            detailController.listUcEvidences(id)
        ]);
        const auditData = auditResult.isRight() ? auditResult.value : null;
        const cfEvidences = cfEvidencesResult.isRight() ? cfEvidencesResult.value : [];
        const ucEvidences = ucEvidencesResult.isRight() ? ucEvidencesResult.value : [];
        result.fold(
            (error) => setState((s) => ({ ...s, data: null, auditData, pbiDecomposition, cfEvidences, ucEvidences, isLoading: false, error: error.message })),
            (data) => setState((s) => ({ ...s, data, auditData, pbiDecomposition, cfEvidences, ucEvidences, isLoading: false, error: null }))
        );
    }, [devController, detailController, loadPbiDecomposition, id]);

    const refreshQuietly = useCallback(async () => {
        const [result, pbiDecomposition] = await Promise.all([devController.loadDevelopmentById(id), loadPbiDecomposition()]);
        result.fold(
            () => {},
            (data) => setState((s) => ({ ...s, data, pbiDecomposition }))
        );
    }, [devController, loadPbiDecomposition, id]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const status = state.data?.status ?? null;
        if (previousStatusRef.current === 'EF_EM_VALIDACAO_IA' && status && status !== 'EF_EM_VALIDACAO_IA') {
            if (status === 'EF_VALIDADA_IA' || status === 'EF_REJEITADA_IA') {
                setAnalysisJustCompleted(true);
            }
        }
        previousStatusRef.current = status;
    }, [state.data?.status]);

    const isPbiProcessing = state.pbiDecomposition?.isProcessing() ?? false;
    const isAiAnalysisInProgress = state.data?.status === 'EF_EM_VALIDACAO_IA';
    const isPollingActive = isPbiProcessing || isAiAnalysisInProgress;
    const jiraPending = state.data
        ? isJiraSyncPending(
              {
                  epic: state.data.jiraEpicSync,
                  stories: state.data.jiraStoriesSync,
                  solicitations: state.data.solicitations.map((solicitation) => solicitation.jiraSync ?? { status: 'NONE', nextAttemptAt: null })
              },
              new Date()
          )
        : false;
    useJiraSyncPolling(!isPollingActive && jiraPending, refreshQuietly);

    useEffect(() => {
        if (!isPollingActive) {
            return;
        }
        refreshQuietly();
        const interval = setInterval(() => {
            refreshQuietly();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isPollingActive, refreshQuietly]);

    const cancelDevelopment = useCallback(
        async (reasonId: string, justification: string) => {
            const result = await devController.cancelDevelopment(id, reasonId, justification);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [devController, id, load]
    );

    const reactivateDevelopment = useCallback(async () => {
        const result = await devController.reactivateDevelopment(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [devController, id, load]);

    const impedeDevelopment = useCallback(
        async (reasonId: string, description: string) => {
            const result = await devController.impedeDevelopment(id, reasonId, description);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [devController, id, load]
    );

    const resumeDevelopment = useCallback(async () => {
        const result = await devController.resumeDevelopment(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [devController, id, load]);

    const deleteDevelopment = useCallback(async () => {
        const result = await devController.deleteDevelopment(id);
        return result.isRight();
    }, [devController, id]);

    const updateDevelopment = useCallback(
        async (params: {
            title?: string;
            description?: string;
            businessProcessId?: string | null;
            consultorId?: string;
            usuarioChaveId?: string;
            linkedDevelopmentId?: string | null;
            functionalComplexity?: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
            functionalHours?: number | null;
            category?: DevelopmentCategory | null;
        }) => {
            const result = await devController.updateDevelopment(id, params);
            if (result.isRight()) {
                await load();
                return null;
            }
            return result.value.message;
        },
        [devController, id, load]
    );

    const startDevelopment = useCallback(
        async (startDate: string, developers?: Array<{ userId: string; isLead: boolean }>) => {
            const result = await devController.startDevelopment(id, { startDate, developers });
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [devController, id, load]
    );

    const uploadEf = useCallback(
        async (file: File) => {
            const result = await detailController.uploadEf(id, file);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const deleteEf = useCallback(async () => {
        const result = await detailController.deleteEf(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const downloadEf = useCallback(
        async (version?: number) => {
            const result = await detailController.downloadEf(id, version);
            if (result.isRight()) {
                const { blob, filename } = result.value;
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
            return result.isRight();
        },
        [detailController, id]
    );

    const clearAnalysisJustCompleted = useCallback(() => {
        setAnalysisJustCompleted(false);
    }, []);

    const sendToDevelopment = useCallback(async () => {
        const result = await detailController.sendToDevelopment(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const createEstimate = useCallback(
        async (params: {
            complexity: Complexity;
            estimatedHours: number;
            estimatedDeliveryDate?: string;
            notes?: string;
            developers?: Array<{ userId: string; isLead: boolean }>;
            effortIncreaseReasonId?: string;
            effortIncreaseNotes?: string;
        }) => {
            const result = await detailController.createEstimate(id, params);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const approveEstimate = useCallback(async () => {
        const result = await detailController.approveEstimate(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const submitEstimate = useCallback(async () => {
        const result = await detailController.submitEstimate(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const rejectEstimate = useCallback(
        async (reasonId: string) => {
            const result = await detailController.rejectEstimate(id, reasonId);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const returnEstimateToAnalysis = useCallback(
        async (reason: string, notes?: string) => {
            const result = await detailController.returnEstimateToAnalysis(id, reason, notes);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const assignTechLead = useCallback(
        async (techLeadId: string) => {
            const result = await detailController.assignTechLead(id, techLeadId);
            if (result.isRight()) {
                await load();
                return null;
            }
            return result.value.message;
        },
        [detailController, id, load]
    );

    const setDevelopers = useCallback(
        async (developers: Array<{ userId: string; isLead: boolean }>) => {
            const result = await detailController.setDevelopers(id, developers);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const uploadDocument = useCallback(
        async (type: string, file: File) => {
            const result = await detailController.uploadDocument(id, type, file);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const registerDevEnd = useCallback(
        async (endDate: string) => {
            const result = await detailController.registerDevEnd(id, endDate);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const toggleCutover = useCallback(
        async (cutoverNotRequired: boolean) => {
            const result = await detailController.toggleCutoverNotRequired(id, cutoverNotRequired);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const approveDocumentation = useCallback(async () => {
        const result = await detailController.approveDocumentation(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const rejectDocumentation = useCallback(
        async (reasonId: string, notes: string) => {
            const result = await detailController.rejectDocumentation(id, reasonId, notes);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const uploadValidationEvidence = useCallback(
        async (file: File, type: 'cf' | 'uc') => {
            const result = await detailController.uploadValidationEvidence(id, file, type);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const releaseToUc = useCallback(async () => {
        const result = await detailController.releaseToUc(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const approveUcValidation = useCallback(async () => {
        const result = await detailController.approveUcValidation(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const closeWithoutUc = useCallback(async () => {
        const result = await detailController.closeWithoutUc(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const createSolicitation = useCallback(
        async (params: { type: 'BUG' | 'AJUSTE_MINOR'; title: string; description: string; stepsToReproduce?: string }): Promise<string | null> => {
            const result = await detailController.createSolicitation(id, params);
            if (result.isRight()) {
                await load();
                return result.value.id;
            }
            return null;
        },
        [detailController, id, load]
    );

    const approveSolicitation = useCallback(
        async (solicitationId: string): Promise<{ jiraWarning: string | null } | null> => {
            const result = await detailController.approveSolicitation(id, solicitationId);
            if (result.isLeft()) {
                return null;
            }
            await load();
            return result.value;
        },
        [detailController, id, load]
    );

    const resolveSolicitation = useCallback(
        async (solicitationId: string, notes: string) => {
            const result = await detailController.resolveSolicitation(id, solicitationId, notes);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const rejectSolicitation = useCallback(
        async (solicitationId: string, reasonId: string, notes?: string) => {
            const result = await detailController.rejectSolicitation(id, solicitationId, reasonId, notes);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const reclassifySolicitation = useCallback(
        async (solicitationId: string, newType: string, notes: string) => {
            const result = await detailController.reclassifySolicitation(id, solicitationId, newType, notes);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    const listSolicitationEvidences = useCallback(
        async (solicitationId: string): Promise<SolicitationEvidenceProps[]> => {
            const result = await detailController.listSolicitationEvidences(id, solicitationId);
            if (result.isLeft()) {
                throw result.value;
            }
            return result.value.map((model) => model.toObject());
        },
        [detailController, id]
    );

    const uploadSolicitationEvidences = useCallback(
        async (solicitationId: string, files: File[]): Promise<{ uploaded: number; failed: string[] }> => {
            setUploadingEvidences(true);
            let uploaded = 0;
            const failed: string[] = [];
            try {
                for (const file of files) {
                    const result = await detailController.uploadSolicitationEvidence(id, solicitationId, file);
                    if (result.isRight()) {
                        uploaded += 1;
                    } else {
                        failed.push(file.name);
                    }
                }
            } finally {
                setUploadingEvidences(false);
            }
            return { uploaded, failed };
        },
        [detailController, id]
    );

    const removeSolicitationEvidence = useCallback(
        async (solicitationId: string, docId: string) => {
            const result = await detailController.removeSolicitationEvidence(id, solicitationId, docId);
            return result.isRight();
        },
        [detailController, id]
    );

    const downloadSolicitationEvidence = useCallback(
        async (solicitationId: string, docId: string) => {
            const result = await detailController.getSolicitationEvidenceDownloadUrl(id, solicitationId, docId);
            if (result.isRight()) {
                window.open(result.value.url, '_blank', 'noopener,noreferrer');
            }
        },
        [detailController, id]
    );

    const downloadDocument = useCallback(
        async (documentId: string) => {
            try {
                const result = await detailController.downloadDocument(id, documentId);
                if (result.isRight()) {
                    window.open(result.value.url, '_blank', 'noopener,noreferrer');
                }
                return result.isRight();
            } catch {
                return false;
            }
        },
        [detailController, id]
    );

    const retryPbiDecomposition = useCallback(async () => {
        const result = await detailController.retryPbiDecomposition(id);
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [detailController, id, load]);

    const removeValidationEvidence = useCallback(
        async (type: 'cf' | 'uc', evidenceId: string) => {
            const result = await detailController.removeValidationEvidence(id, type, evidenceId);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [detailController, id, load]
    );

    return {
        ...state,
        reload: load,
        analysisJustCompleted,
        clearAnalysisJustCompleted,
        cancelDevelopment,
        reactivateDevelopment,
        impedeDevelopment,
        resumeDevelopment,
        deleteDevelopment,
        updateDevelopment,
        startDevelopment,
        uploadEf,
        deleteEf,
        downloadEf,
        sendToDevelopment,
        createEstimate,
        approveEstimate,
        submitEstimate,
        uploadDocument,
        registerDevEnd,
        toggleCutover,
        approveDocumentation,
        rejectDocumentation,
        uploadValidationEvidence,
        releaseToUc,
        approveUcValidation,
        closeWithoutUc,
        createSolicitation,
        approveSolicitation,
        rejectSolicitation,
        resolveSolicitation,
        reclassifySolicitation,
        listSolicitationEvidences,
        uploadSolicitationEvidences,
        removeSolicitationEvidence,
        downloadSolicitationEvidence,
        downloadDocument,
        uploadingEvidences,
        retryPbiDecomposition,
        rejectEstimate,
        returnEstimateToAnalysis,
        assignTechLead,
        setDevelopers,
        removeValidationEvidence
    };
}
